import React, { PureComponent } from 'react';
import { NormalizedSeries, IChartSeries, IChartGroup } from 'types';
import memoize from 'memoize-one';
// Components
import {
  getNormalizeSeries,
  LABEL_WIDTH,
  SERIES_DIVIDER_HEIGHT,
  CHART_PADDING,
  DEFAULT_CHART_HEIGHT
} from './utils';
import Measure from 'react-measure';
import ChartLine from './chart-line';
import styles from './chart.scss';
import ChartNavigator from './chart-navigator';
import ChartXAxis from './chart-x-axis';
import Mouse from 'core/mouse';
import ChartLabels from './chart-labels';
import { clamp } from 'helpers/math';
import DragOverlay from 'components/drag-overlay';
import ChartSeriesGroup from './chart-series-group';
import ChartPath from './chart-path';
import PointTooltip from './point-tooltip';

interface ChartProps {
  series: IChartSeries[];
  onRange: (fromTimestamp: number, toTimestamp: number) => void;
  onPointHover: (timestamp: number) => void;
  onPointClick: (timestamp: number) => void;
}

const getChartHeight = (hegiht: number): number => {
  return hegiht - CHART_PADDING * 2 - SERIES_DIVIDER_HEIGHT;
};
export default class Chart extends PureComponent<ChartProps> {
  state = {
    minX: 0,
    maxX: 0,
    offset: 0,
    range: [0, 1],
    maxPoints: 100,
    bounds: { width: 100, height: 100 },
    groups: JSON.parse(localStorage.getItem('chartgroups') || '[]')
  };

  _groups = memoize((series: NormalizedSeries[], groups: IChartGroup[]) => {
    const seriesGroups = groups.slice();
    const usedIds = {};
    for (let group of groups) {
      for (let id of group.series) {
        usedIds[id] = true;
      }
    }

    for (let s of series) {
      if (!usedIds[s.id]) seriesGroups.push({ series: [s.id] });
    }

    return seriesGroups;
  });

  get groups(): NormalizedSeries[][] {
    return this._groups(this.series, this.state.groups);
  }

  get chartWidth() {
    return this.state.bounds.width - LABEL_WIDTH;
  }

  get minX() {
    return this.series.minX;
  }

  get maxX() {
    return this.series.maxX;
  }

  zoomIn = (focusPoint?: number) => this.zoom(0.1, focusPoint);
  zoomOut = (focusPoint?: number) => this.zoom(-0.1, focusPoint);

  zoom(percent: number, focusPoint: number = 0.5): void {
    let [start, end] = this.state.range;
    const dRange = end - start;
    start += dRange * percent * focusPoint;
    end -= dRange * percent * (1 - focusPoint);

    this.initRange = [Math.max(0, start), Math.min(end, 1)];
    this.handleRange(this.initRange);
  }

  sortSeries = (s1, s2) => {
    const { seriesOrder } = this.state;
    if (seriesOrder) return seriesOrder.indexOf(s1.id) - seriesOrder.indexOf(s2.id);

    return s1.id < s2.id ? -1 : 1;
  };

  getTimestamp(percent: number) {
    const dx = this.maxX - this.minX;
    return percent * dx + this.minX;
  }

  normalizedSeries = memoize(series => getNormalizeSeries(series).sort(this.sortSeries));
  get series(): NormalizedSeries[] {
    return this.normalizedSeries(this.props.series);
  }

  handleResize = ({ bounds }) => {
    this.setState({ bounds });
  };

  handleMouseMove = (e: MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    this.offset = clamp((e.pageX - rect.left - LABEL_WIDTH) / this.chartWidth, 0, 1);

    if (!this.pendingReq) {
      this.pendingReq = requestAnimationFrame(this.handleOffsetUpdate, 10);
    }
  };

  handleOffsetUpdate = () => {
    this.pendingReq = false;
    this.setState({ offset: this.offset });
    if (this.props.onPointHover) {
      const { range } = this.state;
      const currentPos = range[0] + (range[1] - range[0]) * this.offset;
      this.props.onPointHover(this.getTimestamp(currentPos));
    }
  };

  handleZoomTimeout = (point: number, zoomOut: boolean, timeout: number = 50) => {
    setTimeout(() => {
      if (Mouse.isMouseDown() && Mouse.getDownDist() < 10) {
        if (zoomOut) this.zoomOut(point);
        else this.zoomIn(point);

        this.handleZoomTimeout(point, zoomOut);
      }
    }, timeout);
  };

  handleDragStart = (e: MouseEvent) => {
    // Starting zoom check if mouse is down for more than 700ms and not moving
    const { bounds } = this.state;
    const point = (e.pageX - bounds.left) / bounds.width;

    this.handleZoomTimeout(point, e.altKey, 700);

    this.setState({ dragging: true });
    Mouse.on('mousemove', this.handleDrag);
    Mouse.on('mouseup', this.handleDragEnd);
    this.initRange = this.state.range.slice();
  };

  handleDrag = () => {
    const [start, end] = this.initRange;
    const zoom = end - start;
    const dOffset = clamp(
      (-Mouse.getDownDistX() / this.state.bounds.width) * zoom,
      -start,
      1 - end
    );

    this.handleRange([start + dOffset, end + dOffset]);
  };

  handleDragEnd = () => {
    this.setState({ dragging: false });
    Mouse.off('mousemove', this.handleDrag);
    Mouse.off('mouseup', this.handleDragEnd);
  };

  handleRange = range => {
    this.setState({ range });
    if (this.props.onRange) {
      this.props.onRange(this.getTimestamp(range[0]), this.getTimestamp(range[1]));
    }
  };

  handleGroupUpdate = (groups: IChartGroup[]) => {
    localStorage.setItem('chartgroups', JSON.stringify(groups));
    this.setState({ groups });
  };

  renderSeriesGroup = (group: IChartGroup, groupIndex: number) => {
    // IF we don't find any series with ids from the group then don't render group
    if (!this.series.find(s => group.series.indexOf(s.id) !== -1)) return false;

    const { bounds, offset, range } = this.state;

    const series = this.series.filter(s => group.series.indexOf(s.id) !== -1);
    const height = group.height || DEFAULT_CHART_HEIGHT;
    return (
      <ChartSeriesGroup
        key={groupIndex}
        index={groupIndex}
        height={height}
        series={series}
        groups={this.groups}
        onGroupUpdate={this.handleGroupUpdate}
      >
        <svg
          height={height}
          className={styles.chart}
          onMouseMove={this.handleMouseMove}
          onMouseDown={this.handleDragStart}
          onDoubleClick={this.handleDoubleClick}
        >
          <ChartLine offset={offset} width={bounds.width} height={height} />

          <g transform={`translate(0,${CHART_PADDING})`}>
            <ChartLabels series={series} width={bounds.width} height={getChartHeight(height)}>
              <g transform={`translate(${LABEL_WIDTH}, 0)`}>
                {series.map(s => (
                  <ChartPath
                    key={s.id}
                    color={s.color}
                    points={s.points}
                    width={this.chartWidth}
                    height={getChartHeight(height)}
                    range={range}
                  />
                ))}
                <PointTooltip
                  series={series}
                  current={offset}
                  width={this.chartWidth}
                  height={getChartHeight(height)}
                  range={range}
                />
              </g>
            </ChartLabels>
          </g>
        </svg>
      </ChartSeriesGroup>
    );
  };

  render() {
    const { range, bounds, offset, dragging, sorting } = this.state;
    const series = this.series.sort(this.sortSeries);

    return (
      <Measure bounds onResize={this.handleResize}>
        {({ measureRef }) => (
          <div ref={measureRef} className={styles.root}>
            {/* <DragOverlay show={dragging || sorting} /> */}
            <div className={styles.top}>
              <ChartNavigator
                range={range}
                series={series}
                width={bounds.width}
                onRange={this.handleRange}
              />
              <ChartXAxis
                width={bounds.width}
                minX={this.minX}
                maxX={this.maxX}
                range={range}
                current={offset}
              />
            </div>

            <div className={styles.container}>{this.groups.map(this.renderSeriesGroup)}</div>
          </div>
        )}
      </Measure>
    );
  }
}
